import { EmblaCarousel, emblaPrevNextBtnsClickHandlers, emblaDotBtnsAndClickHandlers, Autoplay } from "@utilities/myntr-bundle";
import { parseConfig, getLazyLoadComponentObserver } from '@utilities/theme';

/**
 * Custom HTML element that wraps the Embla carousel functionality.
 * Provides lazy loading, navigation buttons, and dot indicators for carousel slides.
 *
 * @class EmblaSlider
 * @extends HTMLElement
 *
 * @example
 * <embla-slider class="lazy-load debug" data-options='{"loop": true}'>
 *   <div class="embla__viewport">
 *     <div class="embla__container">
 *       <div class="embla__slide">Slide 1</div>
 *       <div class="embla__slide">Slide 2</div>
 *     </div>
 *   </div>
 *   <button class="embla__button--prev">Previous</button>
 *   <button class="embla__button--next">Next</button>
 *   <div class="embla__dots"></div>
 * </embla-slider>
 */
class EmblaSlider extends HTMLElement {
  /**
   * Creates an instance of EmblaSlider.
   * Initializes lazy loading and parses configuration options from data attributes.
   *
   * @constructor
   */
  constructor() {
    super();
    /** @type {boolean} Whether the slider should be lazy loaded */
    this.lazyLoad = this.classList.contains('lazy-load');
    /** @type {Object} Parsed configuration options for the Embla carousel */
    this.config = parseConfig(this, this.allowDebug());
    this.emblaApi = null;
    if(this.allowDebug()) {
      console.log(`EmblaSlider options:`, this.config.options);
    }
  }

  /**
   * Checks if debug mode is enabled for this slider instance.
   * Debug mode is activated by adding the 'debug' class to the element.
   *
   * @returns {boolean} True if debug mode is enabled
   */
  allowDebug() {
    return this.classList.contains('debug');
  }

  /**
   * Called when the element is added to the DOM.
   * Determines whether to initialize immediately or set up lazy loading.
   */
  connectedCallback() {
    if (this.lazyLoad) {
      if(this.allowDebug()) {
        console.log(`EmblaSlider is lazy-loading`);
      }
      getLazyLoadComponentObserver().observe(this);
    } else {
      if(this.allowDebug()) {
        console.log(`EmblaSlider is ready to initialize`);
      }
      this.init();
    }
  }

  /**
   * Initializes the Embla carousel with navigation controls and dot indicators.
   * Sets up event handlers for previous/next buttons and dot navigation.
   * Configures cleanup handlers to prevent memory leaks when the carousel is destroyed.
   */
  init() {
    if (this.emblaApi) {
      this.emblaApi.reInit();
      return;
    }
    // elements
    const viewportNode = this.querySelector('.embla__viewport');
    const prevBtnNode = this.querySelector('.embla__button--prev');
    const nextBtnNode = this.querySelector('.embla__button--next');
    const dotsNode = this.querySelector('.embla__dots');
    const autoplayPlugin = this.config.plugins?.filter(plugin => plugin.name === 'Autoplay');
    // initialize embla carousel
    let emblaApi;
    if(autoplayPlugin && autoplayPlugin.length > 0) {
      emblaApi = EmblaCarousel(viewportNode, this.config.options, [Autoplay()]);
    } else {
      if(this.allowDebug()) {
        console.log(`EmblaSlider is not using autoplay`);
      }
      emblaApi = EmblaCarousel(viewportNode, this.config.options);
    }
    this.emblaApi = emblaApi;
    // embla plugins
    if(prevBtnNode && nextBtnNode) {
      const removePrevNextBtnsClickHandlers = emblaPrevNextBtnsClickHandlers(
        emblaApi,
        prevBtnNode,
        nextBtnNode
      );
      // cleanup
      emblaApi.on('destroy', removePrevNextBtnsClickHandlers);
    }
    if(dotsNode) {
      const removeDotBtnsAndClickHandlers = emblaDotBtnsAndClickHandlers(
        emblaApi,
        dotsNode
      );
      // cleanup
      emblaApi.on('destroy', removeDotBtnsAndClickHandlers);
    }
  }

  refresh() {
    if (this.emblaApi) {
      this.emblaApi.reInit();
    } else {
      this.init();
    }
  }
}

if(!customElements.get('embla-slider')) {
  customElements.define('embla-slider', EmblaSlider);
}
